import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Lock, User, ShieldAlert } from 'lucide-react';
import { loginAdmin, verifyAdmin } from './adminApi';

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/admin';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // If already logged in, skip login
  useEffect(() => {
    verifyAdmin().then((ok) => { if (ok) navigate(from, { replace: true }); });
  }, [navigate, from]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username || !password) { setError('Ingresa usuario y contraseña'); return; }
    setBusy(true);
    try {
      await loginAdmin(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo iniciar sesión');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f4f5f7] px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.10)] p-8 sm:p-10 border border-[#58677A]/10">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-full bg-[#ED6B24]/10 flex items-center justify-center mb-3">
            <ShieldAlert className="text-[#ED6B24]" size={26} />
          </div>
          <h1 className="text-2xl font-black text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Panel de administración</h1>
          <p className="text-sm text-[#58677A]/70 mt-1">Link Consultores</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#58677A]/80 flex items-center gap-1.5 mb-1.5"><User size={13}/> Usuario</label>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-[#f9fafb] border border-[#58677A]/20 rounded-lg px-4 py-2.5 text-[#58677A] focus:outline-none focus:border-[#ED6B24] focus:bg-white transition"
              placeholder="admin"
              data-testid="admin-login-username"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-[#58677A]/80 flex items-center gap-1.5 mb-1.5"><Lock size={13}/> Contraseña</label>
            <div className="relative">
              <input
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#f9fafb] border border-[#58677A]/20 rounded-lg px-4 py-2.5 pr-11 text-[#58677A] focus:outline-none focus:border-[#ED6B24] focus:bg-white transition"
                placeholder="••••••••"
                data-testid="admin-login-password"
              />
              <button
                type="button"
                onClick={() => setShowPass((s) => !s)}
                aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-md text-[#58677A]/60 hover:text-[#ED6B24] hover:bg-[#ED6B24]/10 transition"
                data-testid="admin-login-toggle-password"
              >
                {showPass ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#ED6B24] hover:bg-[#ff8242] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition"
            data-testid="admin-login-submit"
          >{busy ? 'Ingresando…' : 'Iniciar sesión'}</button>
        </form>

        <p className="text-[11px] text-[#58677A]/50 text-center mt-6">
          5 intentos fallidos bloquean el acceso durante 10 minutos.
        </p>
      </div>
    </div>
  );
}
